import { memo } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { chatHooks } from '~/lib/chat-hooks';
import { chatStore } from '~/lib/stores/chat';
import { workbenchStore } from '~/lib/stores/workbench';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { classNames } from '~/utils/classNames';
import type { QuickActionUnion } from '~/utils/quick-actions';
import { addWorkDir } from '~/utils/workDir';

interface QuickActionProps {
  action: QuickActionUnion;
  disabled?: boolean;
  scrollToBottom?: ScrollToBottom;
}

export const QuickAction = memo<QuickActionProps>(({ action, disabled, scrollToBottom }) => {
  const { type, text } = action;

  const handleClick = () => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.QuickActionUsed,
        properties: {
          action,
        },
      },
    });

    if (type === 'link') {
      window.open(action.href, '_blank');
      return;
    }

    if (type === 'file') {
      workbenchStore.currentView.set('code');
      workbenchStore.setSelectedFile(addWorkDir(action.path));

      return;
    }

    const { message } = action;

    if (!message || !chatHooks.sendMessage) {
      return;
    }

    if (type === 'implement') {
      chatStore.promptMode.set('build');
    } else {
      chatStore.promptMode.set('discussion');
    }

    scrollToBottom?.();
    chatHooks.sendMessage(message.trim());
  };

  return (
    <LegacyButton
      className="flex items-center gap-2"
      variant="primary-light"
      disabled={disabled && (type === 'implement' || type === 'message')}
      size="sm"
      onClick={handleClick}
      role={type === 'link' ? 'link' : 'button'}
    >
      <div
        className={classNames('text-lg', {
          'i-ph:chats': type === 'message',
          'i-ph:code': type === 'implement',
          'i-ph:file -mr-0.5': type === 'file',
          'i-ph:link': type === 'link',
        })}
      />
      {text}
    </LegacyButton>
  );
});
